import { useMemo, useState, type ReactNode } from 'react';
import { Drawer } from 'antd';
import { ExportOutlined, NodeIndexOutlined, RightOutlined } from '@ant-design/icons';

import { Badge, Empty, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { NHAN_TANG } from '../../constants';
import { KhoiLoi } from '../../chung/components/KhoiLoi';
import { useDanhMuc } from '../../chung/hooks/useDanhMuc';
import { useQuyenKyThuat } from '../../chung/hooks/useQuyenNktv';
import { soNguyen } from '../../chung/nghiep-vu/so-thong-ke';
import { TRONG, dayDu } from '../../chung/nghiep-vu/thoi-gian';
import type { BuocVet, ChuoiVet } from '../../chung/nghiep-vu/types-vet';
import { useChuoiBuoc } from '../hooks';
import { useNguonVet } from '../nguon';
import { MA_LLM_GOI, beRongTruc, khoangMs, laLlm, nhanTinhNang, tokenLonNhat } from '../nghiep-vu/vet';
import { BocTachToken } from './BocTachToken';
import { ChiPhi, NhanCanhBao, NhanUocTinh, TietKiemDem } from './NhanTinCay';

/**
 * Waterfall các bước của một chuỗi xử lý — "luồng đi từ đâu tới đâu, tốn token ở bước nào". Port `vet/ChuoiBuoc.tsx`
 * native.
 *
 * ── HAI THANH, HAI CÂU HỎI KHÁC NHAU ────────────────────────────────────
 * • **Thanh thời gian** đặt theo `lech_ms` và dài theo `do_tre_ms`: "chặng nào làm người dùng phải chờ".
 * • **Thanh token** dài theo `tong_token` so với bước tốn nhất của chuỗi: "chặng nào ăn token".
 * Một lượt gọi mô hình 300 ms có thể tốn 90% token của cả chuỗi — gộp vào một thanh là mất đúng phát hiện ấy.
 *
 * Bước không khai `do_tre_ms` thì vẽ một vạch tại `lech_ms` và nói ra trong chú giải — một thanh dài 0 trông như
 * "nhanh tức thì".
 *
 * Liên kết Langfuse: chỉ hiện khi máy chủ trả URL VÀ người xem có quyền kỹ thuật (`audit:ky-thuat:read`) — cùng luật với
 * `chung/components/ChuoiXuLy.tsx`: Langfuse có dữ liệu mọi tenant và có cả prompt/câu trả lời.
 */

const RONG_DRAWER = 'min(1040px, 100vw)';
const TRUC = 1000;
const CAO_THANH = 12;
const RONG_TOI_THIEU = 3;
const RONG_VACH = 4;
const SO_MOC_TRUC = 4;
const LUOI = 'grid grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)_4.5rem_6rem] items-center gap-x-4';

const MAU_BUOC = {
  loi: 'var(--color-danger-500)',
  llm: 'var(--color-violet-500)',
  thuong: 'var(--color-sky-500)',
} as const;

const MAU_TOKEN_BUOC = 'var(--color-orange-500)';

function mauCua(buoc: BuocVet): string {
  if (buoc.ket_qua === 'that_bai') return MAU_BUOC.loi;
  return laLlm(buoc) ? MAU_BUOC.llm : MAU_BUOC.thuong;
}

function ThanhThoiGian({ buoc, be, nhan }: { buoc: BuocVet; be: number; nhan: string }) {
  const x = Math.min((buoc.lech_ms / be) * TRUC, TRUC);
  const coDoDai = buoc.do_tre_ms !== null;
  const rong = coDoDai ? Math.max(Math.min((buoc.do_tre_ms ?? 0) / be, 1) * TRUC, RONG_TOI_THIEU) : RONG_VACH;
  return (
    <svg
      viewBox={`0 0 ${TRUC} ${CAO_THANH}`}
      preserveAspectRatio="none"
      className="block h-3 w-full text-content-muted"
      role="img"
      aria-label={nhan}
    >
      <title>{nhan}</title>
      <line x1={0} y1={CAO_THANH / 2} x2={TRUC} y2={CAO_THANH / 2} stroke="currentColor" strokeOpacity={0.15} />
      <rect
        x={Math.min(x, TRUC - rong)}
        y={coDoDai ? 2 : 0}
        width={rong}
        height={coDoDai ? CAO_THANH - 4 : CAO_THANH}
        rx={coDoDai ? 2 : 1}
        fill={mauCua(buoc)}
      />
    </svg>
  );
}

function ThanhToken({ tongToken, lonNhat, nhan }: { tongToken: number; lonNhat: number; nhan: string }) {
  const rong = Math.max((tongToken / Math.max(lonNhat, 1)) * TRUC, RONG_TOI_THIEU);
  return (
    <svg viewBox={`0 0 ${TRUC} 6`} preserveAspectRatio="none" className="block h-1.5 w-full" role="img" aria-label={nhan}>
      <title>{nhan}</title>
      <rect x={0} y={0} width={rong} height={6} rx={1.5} fill={MAU_TOKEN_BUOC} />
    </svg>
  );
}

function TrucThoiGian({ be }: { be: number }) {
  const { locale } = useT();
  return (
    <div className="flex justify-between font-mono text-2xs text-content-muted">
      {Array.from({ length: SO_MOC_TRUC + 1 }, (_, i) => (
        <span key={i}>{khoangMs((be * i) / SO_MOC_TRUC, locale)}</span>
      ))}
    </div>
  );
}

function MucChiTiet({ nhan, children }: { nhan: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="mt-0.5 break-words text-sm text-content-primary">{children}</dd>
    </div>
  );
}

function ChiTietBuoc({ buoc }: { buoc: BuocVet }) {
  const { t, locale } = useT();
  const llm = laLlm(buoc);
  return (
    <div className="mb-3 ml-7 space-y-4 rounded-lg border border-default bg-subtle p-4">
      {buoc.ket_qua === 'that_bai' && (buoc.ma_loi || buoc.ly_do) && (
        <p className="break-words text-sm text-danger-700">{[buoc.ma_loi, buoc.ly_do].filter(Boolean).join(' — ')}</p>
      )}

      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <MucChiTiet nhan={t('nktvSuDungAi.chuoi.luc')}>
          <time dateTime={buoc.thoi_gian} className="font-mono text-xs">
            {dayDu(buoc.thoi_gian, locale)}
          </time>
        </MucChiTiet>
        <MucChiTiet nhan={t('nktvSuDungAi.chuoi.dichVu')}>
          {buoc.service} · {t(NHAN_TANG[buoc.tang])}
        </MucChiTiet>
        {buoc.doi_tuong_ten && (
          <MucChiTiet nhan={t('nktvSuDungAi.chuoi.doiTuong')}>{buoc.doi_tuong_ten}</MucChiTiet>
        )}
        {llm && (
          <>
            <MucChiTiet nhan={t('nktvSuDungAi.chuoi.moHinh')}>
              {buoc.mo_hinh ?? TRONG}
              {(buoc.nha_cung_cap || buoc.kieu_api) && (
                <span className="block text-xs text-content-muted">
                  {[buoc.nha_cung_cap, buoc.kieu_api].filter(Boolean).join(' · ')}
                </span>
              )}
            </MucChiTiet>
            <MucChiTiet nhan={t('nktvSuDungAi.chuoi.chiPhi')}>
              <ChiPhi
                chiPhi={buoc.chi_phi_ban}
                donVi={buoc.don_vi_tien}
                trangThai={buoc.trang_thai_gia}
                thieuTyGia={buoc.thieu_ty_gia}
                moHinh={buoc.mo_hinh}
              />
            </MucChiTiet>
            <MucChiTiet nhan={t('nktvSuDungAi.chuoi.tietKiem')}>
              <TietKiemDem giaTri={buoc.tiet_kiem_dem} donVi={buoc.don_vi_tien} />
            </MucChiTiet>
          </>
        )}
      </dl>

      {llm && (buoc.canh_bao_token || buoc.uoc_tinh === true) && (
        <div className="flex flex-wrap items-center gap-2">
          {buoc.canh_bao_token && <NhanCanhBao canhBao={buoc.canh_bao_token} />}
          {buoc.uoc_tinh === true && <NhanUocTinh />}
        </div>
      )}

      {llm && (
        <div className="space-y-2 border-t border-default pt-3">
          <p className="text-xs font-medium text-content-secondary">{t('nktvSuDungAi.chuoi.bocTach')}</p>
          <BocTachToken token={buoc.token} tongToken={buoc.tong_token} />
        </div>
      )}
    </div>
  );
}

function DongBuoc({
  buoc,
  be,
  lonNhat,
  mo,
  onDoi,
}: {
  buoc: BuocVet;
  be: number;
  lonNhat: number;
  mo: boolean;
  onDoi: () => void;
}) {
  const { t, locale } = useT();
  const danhMuc = useDanhMuc();
  const nhan =
    buoc.hanh_dong === MA_LLM_GOI
      ? [t('nktvSuDungAi.chuoi.goiMoHinh'), nhanTinhNang(buoc)].filter(Boolean).join(' · ')
      : danhMuc.nhan(buoc.hanh_dong);
  const nhanThanh = [
    t('nktvSuDungAi.chuoi.lech', { lech: khoangMs(buoc.lech_ms, locale) }),
    buoc.do_tre_ms === null
      ? t('nktvSuDungAi.chuoi.khongKhaiDoTre')
      : t('nktvSuDungAi.chuoi.doTre', { doTre: khoangMs(buoc.do_tre_ms, locale) }),
  ].join(' · ');

  return (
    <li className="border-t border-default first:border-t-0">
      <button
        type="button"
        aria-expanded={mo}
        title={t('nktvSuDungAi.chuoi.moChiTiet')}
        onClick={onDoi}
        className={cn(
          LUOI,
          'w-full rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-subtle',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600',
        )}
      >
        <span className="flex min-w-0 items-start gap-2">
          <RightOutlined
            aria-hidden
            className={cn('mt-1 shrink-0 text-2xs text-content-muted transition-transform', mo && 'rotate-90')}
          />
          <span aria-hidden className="mt-1.5 size-2 shrink-0 rounded-full" style={{ backgroundColor: mauCua(buoc) }} />
          <span className="min-w-0">
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-sm text-content-primary">{nhan}</span>
              {buoc.ket_qua === 'that_bai' && <Badge variant="danger">{t('nktv.ketQua.that_bai')}</Badge>}
            </span>
            <span className="block truncate text-xs text-content-muted">
              {buoc.service} · {t('nktvSuDungAi.chuoi.batDauSau', { lech: khoangMs(buoc.lech_ms, locale) })}
            </span>
          </span>
        </span>

        <ThanhThoiGian buoc={buoc} be={be} nhan={nhanThanh} />

        <span className="text-right font-mono text-xs tabular-nums text-content-secondary">
          {buoc.do_tre_ms === null ? TRONG : khoangMs(buoc.do_tre_ms, locale)}
        </span>

        <span className="space-y-1 text-right">
          <span className="block font-mono text-xs tabular-nums text-content-primary">
            {soNguyen(buoc.tong_token, locale)}
          </span>
          {buoc.tong_token !== null && (
            <ThanhToken
              tongToken={buoc.tong_token}
              lonNhat={lonNhat}
              nhan={t('nktvSuDungAi.chuoi.thanhToken', { so: soNguyen(buoc.tong_token, locale) })}
            />
          )}
        </span>
      </button>

      {mo && <ChiTietBuoc buoc={buoc} />}
    </li>
  );
}

function ChuGiaiBuoc() {
  const { t } = useT();
  const muc: Array<{ mau: string; nhan: string }> = [
    { mau: MAU_BUOC.thuong, nhan: t('nktvSuDungAi.chuoi.chuGiaiThuong') },
    { mau: MAU_BUOC.llm, nhan: t('nktvSuDungAi.chuoi.chuGiaiLlm') },
    { mau: MAU_BUOC.loi, nhan: t('nktvSuDungAi.chuoi.chuGiaiLoi') },
    { mau: MAU_TOKEN_BUOC, nhan: t('nktvSuDungAi.chuoi.chuGiaiToken') },
  ];
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 border-t border-default px-2 pt-3">
      {muc.map((m) => (
        <li key={m.nhan} className="flex items-center gap-1.5 text-xs text-content-muted">
          <span aria-hidden className="h-2 w-3 rounded-sm" style={{ backgroundColor: m.mau }} />
          {m.nhan}
        </li>
      ))}
      <li className="text-xs text-content-muted">{t('nktvSuDungAi.chuoi.chuGiaiVach')}</li>
    </ul>
  );
}

function OTomTat({ nhan, so, phu }: { nhan: string; so: string; phu?: string }) {
  return (
    <div className="rounded-lg border border-default bg-surface px-4 py-3">
      <dt className="text-xs text-content-muted">{nhan}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums text-content-primary">{so}</dd>
      {phu ? <p className="mt-0.5 truncate text-xs text-content-muted">{phu}</p> : null}
    </div>
  );
}

function TomTat({ chuoi }: { chuoi: ChuoiVet }) {
  const { t, locale } = useT();
  // Nhân sự nền tảng xem được liên kết Langfuse; tenant cần `audit:ky-thuat:read`.
  const quyenKyThuat = useQuyenKyThuat();
  const coQuyenKyThuat = useNguonVet().nenTang || quyenKyThuat;
  const soLlm = chuoi.buoc.filter(laLlm).length;
  const soLoi = chuoi.buoc.filter((b) => b.ket_qua === 'that_bai').length;
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <OTomTat nhan={t('nktvSuDungAi.chuoi.keoDai')} so={khoangMs(chuoi.keo_dai_ms, locale)} />
        <OTomTat
          nhan={t('nktvSuDungAi.chuoi.soBuoc')}
          so={soNguyen(chuoi.so_buoc, locale)}
          phu={soLoi > 0 ? t('nktvSuDungAi.trace.soLoi', { so: soNguyen(soLoi, locale) }) : undefined}
        />
        <OTomTat nhan={t('nktvSuDungAi.chuoi.luotGoiAi')} so={soNguyen(soLlm, locale)} />
        <OTomTat nhan={t('nktvSuDungAi.chuoi.tongToken')} so={soNguyen(chuoi.tong_token, locale)} />
      </dl>

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs text-content-muted">
        <span>
          {dayDu(chuoi.bat_dau, locale)} → {dayDu(chuoi.ket_thuc, locale)}
        </span>
        <span className="min-w-0">
          {t('nktvSuDungAi.chuoi.maTraCuu')}{' '}
          <span className="select-all break-all font-mono text-content-secondary">{chuoi.trace_id}</span>
        </span>
        {coQuyenKyThuat && chuoi.lien_ket_langfuse && (
          <a
            href={chuoi.lien_ket_langfuse}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 rounded text-content-link hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            <ExportOutlined aria-hidden />
            {t('nktv.nganKeo.moLangfuse')}
          </a>
        )}
      </div>

      {chuoi.bi_cat && (
        <p className="text-xs text-warning-700" role="status">
          {t('nktvSuDungAi.chuoi.biCat')}
        </p>
      )}
    </div>
  );
}

export function ChuoiBuoc({
  traceId,
  tenantId,
  onDong,
}: {
  traceId: string;
  /** Tenant của dòng đã bấm — cổng nền tảng cần nó để đọc các bước. */
  tenantId?: string;
  onDong: () => void;
}) {
  const { t } = useT();
  const truyVan = useChuoiBuoc(traceId, tenantId);
  const chuoi = truyVan.data;

  const be = useMemo(() => (chuoi ? beRongTruc(chuoi) : 1), [chuoi]);
  const lonNhat = useMemo(() => (chuoi ? tokenLonNhat(chuoi) : 0), [chuoi]);

  const [dangMo, setDangMo] = useState<ReadonlySet<BuocVet['id']>>(() => new Set());
  const doiMo = (id: BuocVet['id']) =>
    setDangMo((cu) => {
      const moi = new Set(cu);
      if (!moi.delete(id)) moi.add(id);
      return moi;
    });

  return (
    <Drawer open onClose={onDong} placement="right" size={RONG_DRAWER} title={t('nktvSuDungAi.chuoi.tieuDe')} destroyOnHidden>
      {truyVan.isError ? (
        <KhoiLoi loi={truyVan.error} onThuLai={() => void truyVan.refetch()} />
      ) : truyVan.isPending || !chuoi ? (
        <div className="space-y-3">
          <Skeleton className="h-28" />
          <Skeleton className="h-64" />
        </div>
      ) : chuoi.buoc.length === 0 ? (
        <Empty
          icon={NodeIndexOutlined}
          title={t('nktvSuDungAi.chuoi.rongTieuDe')}
          description={t('nktvSuDungAi.chuoi.rongMoTa')}
        />
      ) : (
        <div className="space-y-4">
          <TomTat chuoi={chuoi} />

          <div className="overflow-x-auto rounded-lg border border-default bg-surface p-3">
            <div className="min-w-[36rem]">
              <div className={cn(LUOI, 'border-b border-default px-2 pb-2 text-xs font-medium text-content-secondary')}>
                <span className="pl-7">{t('nktvSuDungAi.chuoi.cotBuoc')}</span>
                <TrucThoiGian be={be} />
                <span className="text-right">{t('nktvSuDungAi.chuoi.keoDai')}</span>
                <span className="text-right">{t('nktvSuDungAi.chuoi.cotToken')}</span>
              </div>
              <ol>
                {chuoi.buoc.map((buoc) => (
                  <DongBuoc
                    key={buoc.id}
                    buoc={buoc}
                    be={be}
                    lonNhat={lonNhat}
                    mo={dangMo.has(buoc.id)}
                    onDoi={() => doiMo(buoc.id)}
                  />
                ))}
              </ol>
              <ChuGiaiBuoc />
            </div>
          </div>
          <p className="text-xs text-content-muted">{t('nktvSuDungAi.chuoi.goiYMo')}</p>
        </div>
      )}
    </Drawer>
  );
}
